import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Gauge, Activity, ShieldAlert, TrendingUp } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ label, value, sub }) => (
  <div className="fp-tile p-4">
    <p className="text-[10px] uppercase tracking-[0.18em] text-slate-300">{label}</p>
    <p className="font-outfit text-2xl font-semibold text-white fp-num mt-1">{value}</p>
    {sub && <p className="text-xs text-slate-300 mt-1">{sub}</p>}
  </div>
);

export default function AiEconomicsPage() {
  const { getAuthHeader } = useAuth();
  const [u, setU] = useState(null);
  const [routing, setRouting] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/admin/credits/utilization`, getAuthHeader())
      .then(r => setU(r.data)).catch(e => setError(e?.response?.data?.detail || 'Could not load'));
    axios.get(`${API}/admin/ai/routing?hours=720`, getAuthHeader())
      .then(r => setRouting(r.data)).catch(() => {});
  }, [getAuthHeader]);

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="ai-economics-page">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <p className="text-[10px] uppercase tracking-[0.2em] text-cyan-300/80">Admin · AI economics</p>
        <h1 className="font-outfit text-3xl md:text-4xl font-semibold text-white mt-2">
          Where the capacity actually goes.
        </h1>
        <p className="text-sm text-slate-300 mt-2 max-w-2xl">
          Real utilisation per tier, so phase-2 allowance sizing is a decision made on data. Burst
          guard trips are abuse signals, if they show up on ordinary accounts, the window is too tight.
        </p>
      </motion.div>

      {error && <p className="text-sm text-rose-300 mt-6" data-testid="econ-error">{error}</p>}
      {!u && !error && <p className="text-sm text-slate-300 mt-6" data-testid="econ-loading">Reading the wallets…</p>}

      {routing && (
        <div className="grid sm:grid-cols-4 gap-3 mt-7" data-testid="econ-headline">
          <Stat label="Requests (30d)" value={routing.requests?.toLocaleString()} />
          <Stat label="Provider spend" value={`$${(routing.total_cost_usd || 0).toFixed(2)}`}
            sub={`avg $${(routing.avg_cost_per_request || 0).toFixed(5)} / request`} />
          <Stat label="Escalation rate" value={`${routing.escalation_rate ?? 0}%`} />
          <Stat label="Error rate" value={`${routing.error_rate ?? 0}%`} />
        </div>
      )}

      {u && (
        <>
          <div className="glass-tint fp-rail mt-8 p-5 md:p-6" data-testid="utilization-table">
            <div className="flex items-center gap-2">
              <Gauge className="w-4 h-4 text-cyan-300" />
              <h2 className="font-outfit text-xl font-semibold text-white">Utilisation by tier</h2>
            </div>
            <div className="overflow-x-auto mt-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-slate-300">
                    <th className="py-2 pr-4">Plan</th><th className="py-2 pr-4">Price</th>
                    <th className="py-2 pr-4">Allowance</th><th className="py-2 pr-4">Accounts</th>
                    <th className="py-2 pr-4">p50</th><th className="py-2 pr-4">p90</th>
                    <th className="py-2 pr-4">p99</th><th className="py-2 pr-4">Mean</th>
                    <th className="py-2 pr-4">Exhausted</th><th className="py-2">3h burst cap</th>
                  </tr>
                </thead>
                <tbody className="text-slate-300">
                  {u.tiers.map(t => (
                    <tr key={t.plan} className="border-t border-white/5" data-testid={`tier-row-${t.plan}`}>
                      <td className="py-2.5 pr-4 text-white font-medium">{t.plan_name || t.plan}</td>
                      <td className="py-2.5 pr-4">{t.price_monthly ? `$${t.price_monthly}/mo` : '-'}</td>
                      <td className="py-2.5 pr-4 fp-num">{t.allowance.toLocaleString()}</td>
                      <td className="py-2.5 pr-4 fp-num">{t.subscribers}</td>
                      <td className="py-2.5 pr-4 fp-num">{t.p50_percent_used ?? '-'}%</td>
                      <td className="py-2.5 pr-4 fp-num">{t.p90_percent_used ?? '-'}%</td>
                      <td className="py-2.5 pr-4 fp-num">{t.p99_percent_used ?? '-'}%</td>
                      <td className="py-2.5 pr-4 fp-num">{t.mean_percent_used}%</td>
                      <td className="py-2.5 pr-4 fp-num">{t.exhausted_count}</td>
                      <td className="py-2.5 fp-num">{t.burst_window_cap.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-300 mt-4">{u.note}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-6">
            <div className="glass-tint fp-rail p-5" data-testid="burst-events">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-amber-300" />
                <h3 className="font-outfit text-lg font-semibold text-white">
                  Burst guard · {u.burst_guard_events_30d} trips in 30 days
                </h3>
              </div>
              {u.recent_burst_events.length === 0
                ? <p className="text-sm text-slate-300 mt-3">Never fired. Organic usage is nowhere near it.</p>
                : <ul className="mt-3 space-y-2">
                  {u.recent_burst_events.map((e, i) => (
                    <li key={i} className="text-xs text-slate-300">
                      {e.created_at?.slice(0, 16).replace('T', ' ')} · {e.plan} · spent {e.spent_in_window} of {e.cap} in {e.window_hours}h
                    </li>))}
                </ul>}
            </div>

            <div className="glass-tint fp-rail p-5" data-testid="credit-anomalies">
              <div className="flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 text-rose-300" />
                <h3 className="font-outfit text-lg font-semibold text-white">
                  Allowance anomalies · {u.credit_anomalies_30d} in 30 days
                </h3>
              </div>
              <p className="text-xs text-slate-300 mt-2">
                Fires if a paid plan ever resolves to an allowance under 100, the free-tier fallback
                pattern that used to cap paying users at 5 actions a day.
              </p>
              {u.recent_anomalies.length === 0
                ? <p className="text-sm text-emerald-300/90 mt-3">Clean. No paid account is being starved.</p>
                : <ul className="mt-3 space-y-2">
                  {u.recent_anomalies.map((e, i) => (
                    <li key={i} className="text-xs text-rose-300/90">
                      {e.created_at?.slice(0, 16).replace('T', ' ')} · {e.plan} · allowance {e.allowance} · {e.legacy_action || e.action_id}
                    </li>))}
                </ul>}
            </div>
          </div>

          {routing?.by_action && (
            <div className="glass-tint fp-rail p-5 mt-6" data-testid="by-action">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-violet-300" />
                <h3 className="font-outfit text-lg font-semibold text-white">Cost per action (30 days)</h3>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
                {Object.entries(routing.by_action).map(([k, v]) => (
                  <div key={k} className="fp-tile p-3">
                    <p className="text-xs text-white font-medium">{k}</p>
                    <p className="text-xs text-slate-300 mt-1 fp-num">
                      {v.count} runs · ${v.cost_per_execution} each · {v.credits_per_execution} credits
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
